import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { X } from 'lucide-react';
import { useDashboardStudents } from '@/hooks/use-dashboard';
import { useTags } from '@/hooks/use-tags';
import { StudentsTable } from '@/components/students/students-table';
import { StudentFilters } from '@/components/students/student-filters';
import { TagBadge } from '@/components/tags/tag-badge';
import { TagFilterPicker } from '@/components/tags/tag-filter-picker';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

// Parse the `tag_id` query param as an array. Accepts either repeated
// keys (...?tag_id=a&tag_id=b) or a single comma-separated value
// (...?tag_id=a,b). Click-through from /tags and /funnel uses the
// single-value form, the picker uses the comma-joined form.
function readTagIDs(params: URLSearchParams): string[] {
  const out = new Set<string>();
  for (const raw of params.getAll('tag_id')) {
    for (const part of raw.split(',')) {
      const trimmed = part.trim();
      if (trimmed !== '') out.add(trimmed);
    }
  }
  return Array.from(out);
}

export default function StudentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const perPage = Number(searchParams.get('per_page') ?? '20');
  const status = searchParams.get('status') ?? 'all';
  const sortBy = searchParams.get('sort_by') ?? 'name';
  const sortOrder = searchParams.get('sort_order') ?? 'asc';
  const searchQuery = searchParams.get('search') ?? '';
  const activeTagIDs = useMemo(() => readTagIDs(searchParams), [searchParams]);

  // Local draft for the search input so typing feels instant. Flushed to
  // the URL + the API call on a 300 ms debounce so we don't hammer the
  // backend on every keystroke.
  const [searchDraft, setSearchDraft] = useState(searchQuery);
  useEffect(() => {
    setSearchDraft(searchQuery);
  }, [searchQuery]);
  useEffect(() => {
    if (searchDraft === searchQuery) return;
    const t = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchDraft.trim() === '') {
          next.delete('search');
        } else {
          next.set('search', searchDraft.trim());
        }
        next.set('page', '1');
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const { data, isLoading } = useDashboardStudents({
    page,
    per_page: perPage,
    status: status === 'all' ? undefined : status,
    sort_by: sortBy,
    sort_order: sortOrder,
    search: searchQuery || undefined,
    tag_id: activeTagIDs.length > 0 ? activeTagIDs : undefined,
  });

  // Cached tag list shared with /tags and /funnel — used to render chip
  // badges for the active filter set.
  const { data: allTags = [] } = useTags();
  const activeTags = useMemo(
    () => activeTagIDs.map((id) => allTags.find((t) => t.id === id)).filter(Boolean),
    [allTags, activeTagIDs],
  );

  const updateParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      if (key !== 'page') next.set('page', '1');
      return next;
    });
  };

  const toggleTag = (tagID: string) => {
    const nextTags = activeTagIDs.includes(tagID)
      ? activeTagIDs.filter((id) => id !== tagID)
      : [...activeTagIDs, tagID];
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('tag_id');
      if (nextTags.length > 0) next.set('tag_id', nextTags.join(','));
      next.set('page', '1');
      return next;
    });
  };

  const clearAllTags = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('tag_id');
      next.set('page', '1');
      return next;
    });
  };

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <StudentFilters
          status={status}
          sortBy={sortBy}
          sortOrder={sortOrder}
          searchDraft={searchDraft}
          onStatusChange={(v) => v && updateParam('status', v)}
          onSortByChange={(v) => v && updateParam('sort_by', v)}
          onSortOrderToggle={() => updateParam('sort_order', sortOrder === 'asc' ? 'desc' : 'asc')}
          onSearchChange={setSearchDraft}
          onSearchClear={() => {
            setSearchDraft('');
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete('search');
              next.set('page', '1');
              return next;
            });
          }}
        />
        <TagFilterPicker
          selectedIds={activeTagIDs}
          onToggle={toggleTag}
          onClear={clearAllTags}
        />
      </div>

      {activeTagIDs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Filtered by tag:</span>
          {activeTags.map((tag) =>
            tag ? (
              <TagBadge
                key={tag.id}
                tag={tag}
                onRemove={() => toggleTag(tag.id)}
              />
            ) : null,
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {data?.total ?? 0} matching
          </span>
          {activeTagIDs.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllTags}
              title="Clear all tag filters"
            >
              <X className="mr-1 h-3 w-3" />
              Clear all
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <StudentsTable students={data?.students ?? []} />
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updateParam('page', String(Math.max(1, page - 1)))}
                    aria-disabled={page <= 1}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = i + 1;
                  return (
                    <PaginationItem key={p}>
                      <PaginationLink
                        isActive={p === page}
                        onClick={() => updateParam('page', String(p))}
                        className="cursor-pointer"
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => updateParam('page', String(Math.min(totalPages, page + 1)))}
                    aria-disabled={page >= totalPages}
                    className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
